/**
 * IPA's own field names, for the output fields LSEG's metadata does not know.
 *
 * IPA's field names are case-sensitive: `Cheapesttodeliver` comes back InvalidField. EAV sends
 * the names LSEG's metadata knows in IPA's spelling (fields.generated.ts), but that list is
 * LSEG's 2022 one: a bond future's Price, CheapestToDeliver, ConversionFactor are not in it, and
 * LSEG's RDP.Analytics answers them in any case all the same (2026-10-28). So when IPA flags a
 * requested field InvalidField, EAV asks IPA for the instrument's field list (a getFields
 * request: no fields, Headers only), respells the fields from it and sends the request again.
 * A list is fetched once per kind of instrument and session.
 */

import type { AnalyticsBody } from "./request";
import type { AnalyticsAnswer } from "./response";

const lists = new Map<string, Promise<string[] | null>>();

/** Forget every fetched list (tests). */
export function clearIpaFieldLists(): void {
  lists.clear();
}

/** Whether IPA flagged a requested field InvalidField in any of the answers. */
export function hasInvalidField(answers: unknown[]): boolean {
  return answers.some((a) =>
    ((a as AnalyticsAnswer | null | undefined)?.headers ?? []).some((h) => String(h?.type).toLowerCase() === "invalidfield"),
  );
}

/** The request for IPA's field list of the batch's first instrument, in LSEG's getFields shape. */
export function fieldListRequest(batch: AnalyticsBody): AnalyticsBody {
  return { universe: batch.universe.slice(0, 1), fields: [], outputs: ["Headers"] };
}

/** The field names of IPA's answer to a field-list request, or null when it has no headers. */
export function fieldListNames(answer: unknown): string[] | null {
  const headers = (answer as AnalyticsAnswer | null | undefined)?.headers;
  if (!Array.isArray(headers)) return null;
  return headers.map((h) => h?.name).filter((n): n is string => typeof n === "string" && n !== "");
}

/** The fields in the list's spelling (case-insensitive), a repeat sent once; null when nothing changes. */
export function respell(fields: string[], names: string[]): string[] | null {
  const byLower = new Map(names.map((n) => [n.toLowerCase(), n]));
  const out = Array.from(new Set(fields.map((f) => byLower.get(f.toLowerCase()) ?? f)));
  return out.length === fields.length && out.every((f, i) => f === fields[i]) ? null : out;
}

/** IPA's field list for one kind of instrument: fetched once, a failed or empty fetch forgotten. */
export function ipaFieldList(key: string, fetch: () => Promise<string[] | null>): Promise<string[] | null> {
  let list = lists.get(key);
  if (!list) {
    list = fetch()
      .catch(() => null)
      .then((names) => {
        if (!names) lists.delete(key);
        return names;
      });
    lists.set(key, list);
  }
  return list;
}
